import { Bucket } from '@src/components/Bucket';
import { CardControlRow } from '@src/components/MarketDataCard/components/CardControlRow';
import { getMarketSelectorAggregationLabel } from '@src/components/MarketSelector/presets/getMarketSelectorAggregationLabel';
import { useFavoriteAggregationPresets } from '@src/components/MarketSelector/presets/useFavoriteAggregationPresets';
import { useMarketSelectorAggregationPresets } from '@src/components/MarketSelector/presets/useMarketSelectorAggregationPresets';
import { getSelectionsFromPreset } from '@src/components/MarketSelector/useMarketSelector';
import { useOrderPresetSearchSelectOptions } from '@src/components/OrderPresets/OrderPresetSearchSelect';
import { useSubAccountSearchSelectOptions } from '@src/components/SubAccounts/useSubAccountSearchSelectOptions';
import { useSubAccounts } from '@src/providers';
import { useTradingSettings } from '@src/providers/AppConfigProvider';
import { useOrderPresets, type IOrderPreset } from '@src/providers/OrderPresetsContext';
import { useRfdDragIslandProps } from '@src/providers/RfdDragIslandContext';
import {
  Box,
  Grid,
  MixpanelEvent,
  MixpanelEventSource,
  useAggregationsContext,
  useCallbackRef,
  useMarketAccountsContext,
  useMixpanel,
  type ISubaccount,
  type PredictionMarketCardConfig,
  type Security,
} from '@talos/kyoko';
import { useMemo, useRef, useState } from 'react';

import { getPredictionMarketAccountsForMarket } from './predictionCardMarkets';
import { PredictionCardMarketSelectorPanel } from './PredictionCardMarketSelectorPanel';
import { usePredictionMarketDataCardMarketSelectorItems } from './usePredictionMarketDataCardMarketSelectorItems';

interface PredictionCardControlsProps {
  config: PredictionMarketCardConfig;
  quantity: string | undefined;
  securities: readonly Security[];
  onSave: (config: PredictionMarketCardConfig) => void;
}

export function PredictionCardControls({ config, quantity, securities, onSave }: PredictionCardControlsProps) {
  const mixpanel = useMixpanel(MixpanelEventSource.PredictionMarketCard);
  const rfdDragIslandProps = useRfdDragIslandProps();
  const controlsRef = useRef<HTMLDivElement>(null);
  const [isMarketSelectorOpen, setIsMarketSelectorOpen] = useState(false);
  const [hasOpenedMarketSelector, setHasOpenedMarketSelector] = useState(false);
  const { enableDefaultMarketCardSubAccount } = useTradingSettings();
  const { marketAccountsByMarket, isMarketAccountActive } = useMarketAccountsContext();
  const { aggregationsByName } = useAggregationsContext();
  const { aggregationPresetsByID } = useMarketSelectorAggregationPresets();
  const { subAccountsByName } = useSubAccounts();
  const subAccountOptions = useSubAccountSearchSelectOptions();
  const { orderPresetsByID, orderPresetsList, updateOrderPreset } = useOrderPresets();
  const primarySecurity = securities[0];
  const orderPresetOptions = useOrderPresetSearchSelectOptions({ security: primarySecurity });
  const activeMarketAccounts = useMemo(
    () => getPredictionMarketAccountsForMarket(config.market, marketAccountsByMarket).filter(isMarketAccountActive),
    [config.market, isMarketAccountActive, marketAccountsByMarket]
  );
  const marketSelectorItems = usePredictionMarketDataCardMarketSelectorItems({
    outcome: config.outcomeSymbol ? primarySecurity : undefined,
    market: config.market,
    marketAccounts: activeMarketAccounts,
  });
  const marketSelectorItemsByName = useMemo(
    () => new Map(marketSelectorItems.map(item => [item.name, item])),
    [marketSelectorItems]
  );
  const favoriteAggregationPresets = useFavoriteAggregationPresets(marketSelectorItemsByName);
  const resolvedAggregationLabel = getMarketSelectorAggregationLabel(
    config.selectedAggregation,
    aggregationPresetsByID,
    aggregationsByName
  );

  const handleQuantityChange = useCallbackRef((_index: number, nextQuantity: string) => {
    const trimmedQuantity = nextQuantity.trim() || undefined;
    if (trimmedQuantity !== quantity) {
      onSave({ ...config, quantity: trimmedQuantity });
    }
  });
  const handleMarketAccountsChange = useCallbackRef(
    (marketAccounts: string[], selectedAggregation: string | undefined) => {
      onSave({ ...config, marketAccounts, selectedAggregation });
    }
  );
  const handleOpenMarketSelector = useCallbackRef(() => {
    setHasOpenedMarketSelector(true);
    setIsMarketSelectorOpen(true);
  });
  const handleCloseMarketSelector = useCallbackRef(() => {
    setIsMarketSelectorOpen(false);
  });
  const handleChangeAggregation = useCallbackRef((aggregationID: string) => {
    const preset = aggregationPresetsByID?.get(aggregationID);
    if (!preset) {
      return;
    }
    mixpanel.track(MixpanelEvent.SelectAggregation, { source: MixpanelEventSource.PredictionMarketCard });
    const marketAccounts = getSelectionsFromPreset(preset, marketSelectorItemsByName).map(item => item.name);
    handleMarketAccountsChange(marketAccounts, aggregationID);
  });
  const handleOrderPresetChange = useCallbackRef((orderPreset?: IOrderPreset) => {
    mixpanel.track(MixpanelEvent.SelectOrderPreset, { source: MixpanelEventSource.PredictionMarketCard });
    onSave({ ...config, orderPresetID: orderPreset?.id });
  });
  const handleToggleFavoritePreset = useCallbackRef((orderPreset: IOrderPreset) => {
    updateOrderPreset(orderPreset.id, { ...orderPreset, isFavorite: !orderPreset.isFavorite });
  });
  const handleSubAccountChange = useCallbackRef((subAccount?: ISubaccount) => {
    mixpanel.track(MixpanelEvent.SelectSubAccount, { source: MixpanelEventSource.PredictionMarketCard });
    onSave({ ...config, subAccount: subAccount?.Name });
  });

  const orderPresetSelection = config.orderPresetID == null ? undefined : orderPresetsByID.get(config.orderPresetID);
  const subAccountSelection = config.subAccount ? subAccountsByName.get(config.subAccount) : undefined;
  const showOrderPresets = orderPresetsList.length > 0;
  const showSubAccountSelector = enableDefaultMarketCardSubAccount && subAccountOptions.length > 0;
  const marketSelectorButtonLabel = config.selectedAggregation
    ? (resolvedAggregationLabel ?? config.selectedAggregation)
    : 'Custom';

  return (
    <Box ref={controlsRef} px="spacing16" data-testid="prediction-card-controls" {...(rfdDragIslandProps ?? {})}>
      <Grid gridTemplateColumns="repeat(3, minmax(0, 1fr))" gap="spacing4" mb="spacing4">
        <Box gridColumn="2" data-testid="prediction-card-quantity">
          <Bucket
            rawValue={quantity}
            increment={primarySecurity?.DefaultSizeIncrement}
            min={primarySecurity?.MinimumSize}
            onUpdateSize={handleQuantityChange}
            shouldExpand={false}
            shouldHighlightOnClick={false}
            shouldShowFullValue
            width="100%"
          />
        </Box>
      </Grid>
      <CardControlRow
        showMarketSelectorOnMobile
        showOrderPresets={showOrderPresets}
        showSubAccountSelector={showSubAccountSelector}
        onMarketSelectorClick={handleOpenMarketSelector}
        aggregationPresets={favoriteAggregationPresets}
        selectedAggregationID={config.selectedAggregation}
        onChangeAggregation={handleChangeAggregation}
        marketSelectorButtonLabel={marketSelectorButtonLabel}
        selectedMarketOrAccountNames={config.marketAccounts ?? []}
        orderPresetSelection={orderPresetSelection}
        onChangeOrderPreset={handleOrderPresetChange}
        onToggleFavoritePreset={handleToggleFavoritePreset}
        orderPresetOptions={orderPresetOptions}
        subAccountSelection={subAccountSelection}
        subAccountOptions={subAccountOptions}
        onSubAccountChange={handleSubAccountChange}
      />
      {hasOpenedMarketSelector ? (
        <PredictionCardMarketSelectorPanel
          items={marketSelectorItems}
          selectedMarketAccounts={config.marketAccounts ?? []}
          selectedAggregation={config.selectedAggregation}
          onChangeSelectedMarketAccounts={handleMarketAccountsChange}
          isOpen={isMarketSelectorOpen}
          onClose={handleCloseMarketSelector}
          panelAnchorRef={controlsRef}
          data-testid="prediction-card-market-account-panel"
        />
      ) : null}
    </Box>
  );
}
